import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { scrollToSection, LOGO_URL } from "@/lib/scroll";

const LINKS = [
  { label: "Services", hash: "#services", testid: "nav-link-services" },
  { label: "Manifesto", hash: "#manifesto", testid: "nav-link-manifesto" },
  { label: "Work", hash: "#work", testid: "nav-link-work" },
  { label: "Testimonials", hash: "#testimonials", testid: "nav-link-testimonials" },
  { label: "Contact", hash: "#contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(hash), 450);
    } else {
      scrollToSection(hash);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.__lenis?.scrollTo(0), 100);
    } else {
      window.__lenis?.scrollTo(0);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#060B18]/85 border-b border-amber-500/15"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-brand-logo"
          onClick={goHome}
          className="flex items-center gap-3 group"
        >
          <span className="block h-10 w-10 rounded-sm overflow-hidden ring-1 ring-amber-400/40 group-hover:ring-amber-300 transition-shadow">
            <img src={LOGO_URL} alt="Build Right Partners" className="h-full w-full object-cover" />
          </span>
          <span className="hidden sm:flex flex-col items-start leading-none whitespace-nowrap">
            <span className="font-serif font-semibold text-amber-50 tracking-wide text-lg">BUILD RIGHT</span>
            <span className="font-mono text-[9px] tracking-[0.35em] text-amber-400/80">PARTNERS</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={l.testid}
              onClick={() => go(l.hash)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-amber-300 transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <Link
            data-testid="nav-link-pricing"
            to="/pricing"
            className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-amber-300 transition-colors duration-300"
          >
            Pricing
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <a
            data-testid="header-phone-link"
            href="tel:+18339957227"
            className="hidden xl:inline-flex items-center gap-2 font-mono text-xs tracking-[0.1em] text-amber-200 hover:text-amber-300 transition-colors whitespace-nowrap"
          >
            <Phone size={14} className="text-amber-400" />
            1-833-995-7227
          </a>
          <Link
            data-testid="nav-button-admin-login"
            to="/admin"
            className="hidden md:block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-amber-300 transition-colors"
          >
            Admin
          </Link>
          <button
            data-testid="nav-button-cta"
            onClick={() => go("#contact")}
            className="hidden sm:inline-flex items-center rounded-full border border-amber-400/50 px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-300 hover:bg-amber-400 hover:text-[#060B18] transition-colors duration-300"
          >
            Book Consultation
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-amber-300"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-amber-500/15 bg-[#060B18]/95 backdrop-blur-xl"
            data-testid="mobile-nav-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.hash}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.hash)}
                  className="text-left font-serif text-2xl text-amber-50 hover:text-amber-300 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <Link
                data-testid="mobile-nav-link-pricing"
                to="/pricing"
                onClick={() => setOpen(false)}
                className="text-left font-serif text-2xl text-amber-50 hover:text-amber-300 transition-colors"
              >
                Pricing
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
